'use strict';

const PREVIEWABLE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/html',
  'text/xml',
  'text/css',
  'text/javascript',
  'application/json',
];

const OFFICE_TYPES = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const COMPRESSED_TYPES = [
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.rar',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
  'application/x-tar',
  'application/gzip',
  'application/x-bzip2',
  'application/x-xz',
];

/** Extension → MIME for types the preview API + browser can handle */
const EXT_TO_PREVIEWABLE_MIME = {
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.html': 'text/html',
  '.htm': 'text/html',
  '.xml': 'text/xml',
  '.css': 'text/css',
  '.js': 'text/javascript',
  '.json': 'application/json',
};

const UNKNOWN_MIME_HINTS = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
  'application/x-download',
]);

/**
 * Guess a concrete MIME from filename for preview decisions when the server sent a generic type.
 * @param {string} fileName
 * @returns {string}
 */
export function inferMimeTypeFromFileName(fileName) {
  if (!fileName || typeof fileName !== 'string') {
    return '';
  }
  const base = fileName.split(/[/\\]/).pop() || '';
  const dot = base.lastIndexOf('.');
  if (dot < 0 || dot === base.length - 1) {
    return '';
  }
  const ext = base.slice(dot).toLowerCase();
  return EXT_TO_PREVIEWABLE_MIME[ext] || '';
}

/**
 * @param {object} file — shape from API / ViewOnlyFileDisplay (`mime_type`, `type`, `name`, `file_name`, `original_name`)
 * @returns {string} MIME string used for previewability (may be inferred from filename)
 */
export function resolveMimeForPreview(file) {
  if (!file || typeof file !== 'object') {
    return '';
  }
  const fileName = file.name || file.file_name || file.original_name || '';
  const mime = String(file.mime_type || file.type || '').trim();
  const lower = mime.toLowerCase();
  if (UNKNOWN_MIME_HINTS.has(lower)) {
    const inferred = inferMimeTypeFromFileName(fileName);
    if (inferred) {
      return inferred;
    }
  }
  return mime;
}

/**
 * @param {string} mimeType
 * @returns {{ isSupported: boolean, isPreviewable: boolean, isCompressed: boolean, isOfficeFile: boolean, recommendDownload: boolean }}
 */
export function getPreviewSupport(mimeType) {
  if (!mimeType || typeof mimeType !== 'string') {
    return {
      isSupported: false,
      isPreviewable: false,
      isCompressed: false,
      isOfficeFile: false,
      recommendDownload: false,
    };
  }

  const normalizedMimeType = mimeType.toLowerCase();

  const isPreviewable = PREVIEWABLE_TYPES.some((type) =>
    normalizedMimeType.includes(type)
  );
  const isCompressed = COMPRESSED_TYPES.some((type) =>
    normalizedMimeType.includes(type)
  );
  const isOfficeFile = OFFICE_TYPES.some((type) =>
    normalizedMimeType.includes(type)
  );

  return {
    isSupported: isPreviewable || isCompressed || isOfficeFile,
    isPreviewable,
    isCompressed,
    isOfficeFile,
    recommendDownload: isCompressed || isOfficeFile,
  };
}

/** Whether this MIME can be opened via the in-app preview URL (PDF, images, plain text family). */
export function isMimePreviewable(mimeType) {
  return getPreviewSupport(mimeType).isPreviewable;
}

/** Per-file: resolve MIME (incl. filename) then apply preview rules — use for each attachment row. */
export function isFilePreviewableByDescriptor(file) {
  return isMimePreviewable(resolveMimeForPreview(file));
}

/** Shown under the filename; hides generic octet-stream when we inferred a concrete type. */
export function getDisplayMimeLabel(file) {
  if (!file || typeof file !== 'object') {
    return '';
  }
  const stored = String(file.mime_type || file.type || '').trim();
  if (stored && !UNKNOWN_MIME_HINTS.has(stored.toLowerCase())) {
    return stored;
  }
  return resolveMimeForPreview(file) || '';
}
